// 🏛️ نظام إدارة المواطن المركزي - تكامل بين جميع الصفحات
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// 👥 بيانات المواطنين الحقيقية - 3 حالات أساسية
const realCitizensData = {
  1001: { 
    id: 1001,
    name: 'فاطمة العنزي', 
    region: 'المنطقة الشرقية', 
    city: 'الدمام', 
    complaints: 1,
    gender: 'أنثى',
    age: 35,
    maritalStatus: 'متزوج',
    email: 'fatima.alanzi@example.com',
    phone: '568040250',
    profile: 'domestic_violence_victim',
    riskLevel: 85,
    issueType: 'عنف أسري',
    description: 'تحتاج حماية عاجلة ودعم نفسي'
  },
  1002: { 
    id: 1002,
    name: 'أحمد المالكي', 
    region: 'الرياض', 
    city: 'الرياض', 
    complaints: 1,
    gender: 'ذكر',
    age: 34,
    maritalStatus: 'متزوج',
    email: 'ahmed.almalki@example.com',
    phone: '580863193',
    profile: 'license_issue',
    riskLevel: 30,
    issueType: 'رخصة عمل',
    description: 'يحتاج تجديد رخصة العمل'
  },
  1003: { 
    id: 1003,
    name: 'سارة الأحمدي', 
    region: 'مكة المكرمة', 
    city: 'جدة', 
    complaints: 1,
    gender: 'أنثى',
    age: 28,
    maritalStatus: 'أعزب',
    email: 'sara.alahmadi@example.com',
    phone: '575935868',
    profile: 'password_reset',
    riskLevel: 10,
    issueType: 'نسيت كلمة المرور',
    description: 'تحتاج إعادة تعيين كلمة مرور أبشر'
  }
};

// 🚨 تنبيهات استباقية حقيقية
const realPredictiveAlerts = {
  1001: { type: 'حالة_طوارئ', confidence: 0.95, priority: 'حرج' },
  1002: { type: 'خدمة_عادية', confidence: 0.60, priority: 'متوسط' },
  1003: { type: 'مساعدة_تقنية', confidence: 0.40, priority: 'منخفض' }
};

// إنشاء السياق
const CitizenContext = createContext();

// مزود السياق
export const CitizenProvider = ({ children }) => {
  const [selectedCitizen, setSelectedCitizen] = useState(null);
  const [citizenHistory, setCitizenHistory] = useState([]);
  const [activeSession, setActiveSession] = useState(null);

  // تحديد المواطن المختار
  const selectCitizen = (citizenId) => {
    const citizen = realCitizensData[citizenId];
    if (citizen) {
      setSelectedCitizen(citizen);
      
      // إضافة إلى التاريخ
      setCitizenHistory(prev => {
        const filtered = prev.filter(c => c.id !== citizenId);
        return [citizen, ...filtered].slice(0, 5); // آخر 5 مواطنين
      });

      // إنشاء جلسة نشطة
      setActiveSession({
        citizenId,
        startTime: new Date(),
        interactions: [],
        currentPage: 'portal'
      });

      // حفظ في localStorage
      localStorage.setItem('selectedCitizen', JSON.stringify(citizen));
      localStorage.setItem('activeSession', JSON.stringify({
        citizenId,
        startTime: new Date(),
        interactions: []
      }));
    }
  };

  // إضافة تفاعل جديد
  const addInteraction = useCallback((interaction) => {
    setActiveSession(prevSession => {
      if (prevSession) {
        const updatedSession = {
          ...prevSession,
          interactions: [...prevSession.interactions, {
            ...interaction,
            timestamp: new Date()
          }]
        };
        localStorage.setItem('activeSession', JSON.stringify(updatedSession));
        return updatedSession;
      }
      return prevSession;
    });
  }, []);

  // تغيير الصفحة النشطة
  const setActivePage = useCallback((page) => {
    setActiveSession(prevSession => {
      if (prevSession && prevSession.currentPage !== page) {
        return {
          ...prevSession,
          currentPage: page
        };
      }
      return prevSession;
    });
  }, []);

  // مسح الاختيار
  const clearSelection = useCallback(() => {
    console.log('بدء مسح الاختيار...');
    setSelectedCitizen(null);
    setActiveSession(null);
    setCitizenHistory([]);
    
    // مسح قوي من localStorage
    try {
      localStorage.removeItem('selectedCitizen');
      localStorage.removeItem('activeSession');
      localStorage.removeItem('citizenHistory');
      console.log('تم مسح localStorage بنجاح');
    } catch (error) {
      console.error('خطأ في مسح localStorage:', error);
    }
    
    console.log('تم مسح الاختيار بالكامل');
    
    // إجبار إعادة تحديث الصفحة إذا لزم الأمر
    setTimeout(() => {
      if (localStorage.getItem('selectedCitizen')) {
        console.log('فشل المسح، إعادة المحاولة...');
        localStorage.clear();
        window.location.reload();
      }
    }, 100);
  }, []);

  // استرداد البيانات عند التحميل
  useEffect(() => {
    const savedCitizen = localStorage.getItem('selectedCitizen');
    const savedSession = localStorage.getItem('activeSession');
    
    if (savedCitizen) {
      setSelectedCitizen(JSON.parse(savedCitizen));
    }
    
    if (savedSession) {
      setActiveSession(JSON.parse(savedSession));
    }
  }, []);

  // الحصول على تنبيه المواطن
  const getCitizenAlert = (citizenId) => {
    return realPredictiveAlerts[citizenId] || null;
  };

  // الحصول على جميع المواطنين
  const getAllCitizens = () => {
    return Object.values(realCitizensData);
  };

  // البحث عن مواطن بالاسم
  const searchCitizenByName = (name) => {
    return Object.values(realCitizensData).find(citizen => 
      citizen.name.includes(name) || 
      citizen.name.split(' ')[0].includes(name)
    );
  };

  // الحصول على إحصائيات المواطن
  const getCitizenStats = () => {
    if (!selectedCitizen) return null;
    
    const alert = getCitizenAlert(selectedCitizen.id);
    return {
      ...selectedCitizen,
      alert,
      sessionDuration: activeSession ? 
        Math.floor((new Date() - new Date(activeSession.startTime)) / 1000 / 60) : 0,
      totalInteractions: activeSession ? activeSession.interactions.length : 0
    };
  };

  const value = {
    // البيانات
    selectedCitizen,
    citizenHistory,
    activeSession,
    allCitizens: realCitizensData,
    
    // الوظائف
    selectCitizen,
    clearSelection,
    addInteraction,
    setActivePage,
    getCitizenAlert,
    getAllCitizens,
    searchCitizenByName,
    getCitizenStats,
    
    // المساعدات
    isSelected: !!selectedCitizen,
    hasActiveSession: !!activeSession
  };

  return (
    <CitizenContext.Provider value={value}>
      {children}
    </CitizenContext.Provider>
  );
};

// Hook لاستخدام السياق
export const useCitizen = () => {
  const context = useContext(CitizenContext);
  if (!context) {
    throw new Error('useCitizen must be used within a CitizenProvider');
  }
  return context;
};

export default CitizenContext;
